import { defineStore } from 'pinia';
import { http } from '@/shared/httpWrapper.js';
import { getCodeSets, TRDocumentContentLibrary } from '@/shared/helpers/api';
import {
    ContentItem,
    getAllContentItems,
    getAllTrRevisionActions,
    getAllTRDocuments,
    getAllTRRevisions,
    getQueryTemplates,
    TRDocumentRecord,
    TRRevisionAction,
    TRRevisionQueryTemplate,
    TRRevisionRecord
} from '@/apps/trdocument/TRDocumentQueries';
import { ref } from 'vue';
import { CodeSet } from '@/shared/helpers/api/types/entities';

export type TRDocumentWithRevisions = {
    document: TRDocumentRecord;
    revisions: TRRevisionRecord[];
};

export const useTRDocumentStore = defineStore('trDocument', () => {
    const trDocumentList = ref<TRDocumentRecord[]>([]);
    const trRevisionList = ref<TRRevisionRecord[]>([]);
    const contentItemList = ref<ContentItem[] | undefined>();
    const queryTemplates = ref<TRRevisionQueryTemplate[] | undefined>();
    const loadingQueryTemplates = ref<boolean>(false);
    const initializing = ref<boolean>(false);
    const trRevisionCodeSetName = ref<string | undefined>();
    const trRevisionQueryTemplateCodeSetName = ref<string | undefined>();
    const trRevisionActions = ref<TRRevisionAction[]>([]);

    let initialized = false;

    function getTRDocument(identity: string): TRDocumentWithRevisions | undefined {
        const document = trDocumentList.value.find(x => x.identity === identity);
        if (document) {
            return {
                document,
                revisions: trRevisionList.value.filter(x => x.trDocument === identity)
            };
        }
    }

    async function getTRDocumentRevision(identity: string, refresh: boolean = false): Promise<TRRevisionRecord[] | undefined> {
        if (refresh || !trRevisionList.value) {
            trRevisionList.value = await getAllTRRevisions();
        }
        return trRevisionList.value.filter(x => x.trDocument === identity);
    }

    function updateTRRevision(identity: string, callback: (r: TRRevisionRecord) => void): void {
        const revision = trRevisionList.value.find(x => x.identity === identity);
        if (!revision)
            throw Error('Unable to find TRRevision in store');
        callback(revision);
    }

    function setDataFinished(identity: string): void {
        updateTRRevision(identity, r => r.areDataFinished = true);
    }

    function setQueriesFinished(identity: string): void {
        updateTRRevision(identity, r => r.areQueriesFinished = true);
    }

    function setURLsFinished(identity: string): void {
        updateTRRevision(identity, r => r.areURLsFinished = true);
    }

    function setIssueDate(identity: string, issueDate: string): void {
        updateTRRevision(identity, r => r.issueDate = issueDate);
    }

    function setAllFinished(identity: string): void {
        updateTRRevision(identity, r => r.isAllFinished = true);
    }

    async function loadQueryTemplates(trDocument: TRDocumentRecord): Promise<void> {
        loadingQueryTemplates.value = true;
        queryTemplates.value = undefined;
        try {
            queryTemplates.value = await getQueryTemplates(trDocument);
            queryTemplates.value.sort((a, b) => a.sequenceNo > b.sequenceNo ? 1 : -1);
        } finally {
            loadingQueryTemplates.value = false;
        }
    }

    async function loadContentItems(
        trRevision: TRRevisionRecord,
        contentLibrary: TRDocumentContentLibrary
    ): Promise<void> {
        contentItemList.value = undefined;
        contentItemList.value = await getAllContentItems(trRevision, contentLibrary);
    }

    async function init() {
        if (initialized)
            return;
        await loadTRDocuments();
    }
    async function loadTRDocuments(): Promise<void> {
        initializing.value = true;

        try {
            let trRevisionCodeSets: CodeSet[];
            let trRevisionQueryTemplateCodeSets: CodeSet[];

            [
                trDocumentList.value,
                trRevisionList.value,
                trRevisionCodeSets,
                trRevisionQueryTemplateCodeSets,
                trRevisionActions.value
            ] = await Promise.all([
                getAllTRDocuments(),
                getAllTRRevisions(),
                getCodeSets(http, 'TRRevision'),
                getCodeSets(http, 'TRRevisionQueryTemplate'),
                getAllTrRevisionActions()
            ]);

            trRevisionCodeSetName.value = trRevisionCodeSets[0].name;
            trRevisionQueryTemplateCodeSetName.value = trRevisionQueryTemplateCodeSets[0].name;
        } finally {
            initializing.value = false;
        }

        initialized = true;
    }

    return {
        contentItemList,
        initializing,
        loadingQueryTemplates,
        queryTemplates,
        trDocumentList,
        trRevisionCodeSetName,
        trRevisionList,
        trRevisionQueryTemplateCodeSetName,
        revisionActionOptions: trRevisionActions,

        getTRDocument,
        setDataFinished,
        setQueriesFinished,
        setURLsFinished,
        setIssueDate,
        setAllFinished,
        loadContentItems,
        loadQueryTemplates,
        getTRDocumentRevision,
        init
    };
});
